import React from 'react';
import Button from '@mui/material/Button';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../stores/AuthContext';
import './Dashboard.css';

const actionCardSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '0.75rem',
  minWidth: 0,
  width: '100%',
  padding: '1.5rem',
  textAlign: 'center',
  color: 'text.primary',
  backgroundColor: 'background.default',
  border: '2px solid',
  borderColor: 'divider',
  borderRadius: '0.75rem',
  '&:hover': {
    backgroundColor: 'background.paper',
    borderColor: 'primary.main',
  },
} as const;

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Mock data - will be replaced with API calls later
  const stats = {
    dataSources: 5,
    ingestionJobs: 24,
    successfulJobs: 20,
    failedJobs: 4,
    totalTables: 156,
    totalLineage: 89,
  };

  const recentJobs = [
    { id: 1, source: 'Snowflake Production', status: 'success', timestamp: '2 hours ago' },
    { id: 2, source: 'BigQuery Analytics', status: 'success', timestamp: '5 hours ago' },
    { id: 3, source: 'Postgres Main', status: 'running', timestamp: 'Just now' },
    { id: 4, source: 'API Endpoint', status: 'failed', timestamp: '1 day ago' },
  ];

  return (
    <div className="dashboard">
      <nav className="dashboard-nav">
        <div className="nav-container">
          <div className="logo">
            <span className="logo-icon">🐋</span>
            <span className="logo-text">Orca</span>
          </div>
          <div className="nav-links">
            <span className="user-info">Welcome, {user?.username || 'User'}</span>
            <Button variant="outlined" onClick={handleLogout}>
              Logout
            </Button>
          </div>
        </div>
      </nav>

      <main className="dashboard-main">
        <div className="dashboard-container">
          <div className="dashboard-header">
            <h1>Dashboard</h1>
            <p className="dashboard-subtitle">Overview of your metadata ingestion</p>
          </div>

          {/* Stats Grid */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">🔌</div>
              <div className="stat-content">
                <h3 className="stat-value">{stats.dataSources}</h3>
                <p className="stat-label">Data Sources</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">⚙️</div>
              <div className="stat-content">
                <h3 className="stat-value">{stats.ingestionJobs}</h3>
                <p className="stat-label">Total Jobs</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">✅</div>
              <div className="stat-content">
                <h3 className="stat-value">{stats.successfulJobs}</h3>
                <p className="stat-label">Successful</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">❌</div>
              <div className="stat-content">
                <h3 className="stat-value">{stats.failedJobs}</h3>
                <p className="stat-label">Failed</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">📊</div>
              <div className="stat-content">
                <h3 className="stat-value">{stats.totalTables}</h3>
                <p className="stat-label">Tables Tracked</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">🔗</div>
              <div className="stat-content">
                <h3 className="stat-value">{stats.totalLineage}</h3>
                <p className="stat-label">Lineage Links</p>
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="dashboard-section">
            <h2 className="section-title">Recent Ingestion Jobs</h2>
            <div className="jobs-table">
              <table>
                <thead>
                  <tr>
                    <th>Source</th>
                    <th>Status</th>
                    <th>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {recentJobs.map((job) => (
                    <tr key={job.id}>
                      <td>{job.source}</td>
                      <td>
                        <span className={`status-badge status-${job.status}`}>
                          {job.status}
                        </span>
                      </td>
                      <td>{job.timestamp}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="dashboard-section">
            <h2 className="section-title">Quick Actions</h2>
            <div className="quick-actions">
              <Button variant="outlined" sx={actionCardSx} onClick={() => navigate('/app/integrations/snowflake')}>
                <span className="action-icon">❄️</span>
                <span className="action-text">Connect Snowflake</span>
              </Button>
              <Button variant="outlined" sx={actionCardSx}>
                <span className="action-icon">➕</span>
                <span className="action-text">Add Data Source</span>
              </Button>
              <Button variant="outlined" sx={actionCardSx}>
                <span className="action-icon">🚀</span>
                <span className="action-text">Run Ingestion</span>
              </Button>
              <Button variant="outlined" sx={actionCardSx}>
                <span className="action-icon">📈</span>
                <span className="action-text">View Lineage</span>
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;

